import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Play, Pause, Volume2, VolumeX,
  MapPin, Phone, Mail,
  Facebook, Twitter, Instagram, Youtube, X, Music, Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useMusic } from '../context/MusicContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/fb28c3a1-55d7-41f0-be32-2ea609158b1e.png";
const LOGO_YELLOW_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/47eefe68-1dae-481a-a902-450e03018adb.png";
const LOGO_WHITE_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/55eefb63-9a48-423b-a331-1f38c35db357.png";
const BANNER_URL = "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/eclzjemh_A8951128-20A6-4794-912D-102728DA4B34.png";
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/a00ovp2g_616219F3-B876-413F-A219-9B0B431D5D1A.mov";
const VIDEO_POSTER = "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/fiyloscp_BE2AF4AF-964B-422E-BF41-B3DCB177C869.png";

// Banner slider — original + 4 new banners uploaded
const BANNER_SLIDES = [
  { url: BANNER_URL, alt: "Official With The Whistle" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/pbcfmnih_32B304A1-BF44-429D-B52D-8EC2E1B651F5.png", alt: "Official With The Whistle Podcast" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/bn0i7cox_BE2AF4AF-964B-422E-BF41-B3DCB177C869.png", alt: "Watch Games Live Now" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/sp8br3h1_356E28C2-9435-4EC9-AE30-664FC8DEB292.png", alt: "Meet Jeff Jazz Davis" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/dq6a4lx1_3D757D6D-AE00-4CC4-9D48-96B966EAE539.png", alt: "Respect The Whistle" },
];

// Respect the Whistle poem (from bio quote banner)
const RESPECT_POEM = [
  { type: 'heading', text: '"One of the hardest jobs in basketball?', accent: false },
  { type: 'heading', text: 'Officiating the game."', accent: true },
  { type: 'line', text: 'The pace is fast.' },
  { type: 'line', text: 'Everything is happening at once.' },
  { type: 'break' },
  { type: 'line', text: 'You can know every rule in the book…' },
  { type: 'line', text: 'but making that call ', accent: 'in real time?' },
  { type: 'line', text: "That's a ", accentInline: 'different level.' },
  { type: 'break' },
  { type: 'line', text: 'And ', accentInline: 'no matter what', text2: " you call—" },
  { type: 'line', text: 'somebody in that gym' },
  { type: 'line', text: 'saw it a completely different way.' },
  { type: 'break' },
  { type: 'line', text: "That's the reality.", accent: true },
  { type: 'break' },
  { type: 'line', text: 'So respect the ones who' },
  { type: 'line', text: 'step on that floor' },
  { type: 'line', text: 'and take on that' },
  { type: 'line', text: 'responsibility anyway.' },
  { type: 'break' },
  { type: 'closer', text: 'Respect the game.' },
  { type: 'closer', text: 'Respect yourself.' },
  { type: 'closer', text: 'Respect the whistle.' },
];

const PHOTO_ALBUM = [
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/lpqgffpe_IMG_5172.png", caption: "Jeff Jazz Davis — CEO & Founder" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/fzz5wp50_IMG_0253.jpeg", caption: "It's Official — The Team" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/i2gxpdp2_FB_IMG_1542050723637.jpeg", caption: "Game Day Action" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/e4cgf4ev_IMG_1475.png", caption: "On The Court" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/a1j2m16a_EF90EFF3-0E98-48F9-B9F4-D01DC05EBD11.jpeg", caption: "Certified Officials" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/zp51ztmu_IMG_0271.jpeg", caption: "With The Legends" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/m82pcvx7_IMG_0251.jpeg", caption: "Family First" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/d25vdnct_IMG_0263.jpeg", caption: "Building Connections" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/fg7yeazy_IMG_0250.jpeg", caption: "With The Pros" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/bob17ez4_IMG_0252.jpeg", caption: "The Full Squad" },
  { url: "https://customer-assets.emergentagent.com/job_game-invoice/artifacts/db2cioex_IMG_0306.png", caption: "Our Mission" },
];

const SERVICES = [
  { name: "Basketball", emoji: "🏀", color: "from-orange-600 to-red-700", description: "Youth · HS · AAU · Pro-Am" },
  { name: "Football", emoji: "🏈", color: "from-amber-700 to-yellow-800", description: "Youth · High School · Adult" },
  { name: "Soccer", emoji: "⚽", color: "from-emerald-600 to-teal-700", description: "Club · School · Rec" },
  { name: "Volleyball", emoji: "🏐", color: "from-sky-600 to-blue-700", description: "Indoor · Beach · All ages" },
  { name: "Baseball", emoji: "⚾", color: "from-slate-600 to-zinc-700", description: "Little League · HS · Adult" },
  { name: "Softball", emoji: "🥎", color: "from-lime-600 to-green-700", description: "Fastpitch · Slow pitch" },
];

const BIO_PARAGRAPHS = [
  "My name is Jeff Jazz Davis. Born in Muncie, Indiana, raised in Phoenix, Arizona. I went to Thunderbird High School where I excelled in football and track and field, running hurdles — and received a scholarship to ASU.",
  "In 1990, I moved to Denver, CO and began training and mentoring youth in track and field and football, coaching little league and hosting football camps along the way.",
  "I started officiating in 2009 (high school football), then certified in high school basketball in 2010. For two years I traveled the AAU circuit. Between 2010 and 2012, I introduced my oldest son Cassius to officiating at 15.",
  "By the end of 2012, I had trained over 10 young men and 1 young lady as sports officials. In 2015, I founded Official With The Whistle. By then, I had 30+ young officials aged 16–20. We traveled to Utah and Vegas each summer.",
  "2017 was a huge summer in Vegas. In 2019, we had the biggest game on the biggest stage — every major college coach in the country was in attendance.",
  "I built this company to give young men and women life skills and to create economic impact in every community we serve. Today we have 80+ officials and have worked games in New Mexico, Texas, Kansas, Wyoming, Utah, Vegas, Arizona, and California — from adult pro-am to youth developmental.",
  "20+ years of experience and 10+ years in business. Like I say, \"Your game's not official unless you have Official With The Whistle!\""
];

// ============ Homepage Music Section (Apple Music-ish) ============
const HomePlaylistSection = ({ playlist, currentIndex, isPlaying, onPlayPause, onSelect }) => {
  if (playlist.length === 0) return null;
  const track = playlist[currentIndex];
  return (
    <section id="music" className="relative py-12 md:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-black to-zinc-950" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-block px-4 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-xs font-bold tracking-widest uppercase text-amber-400 mb-4">
            🎵 The Soundtrack
          </div>
          <h2 className="font-heading font-black uppercase tracking-tight" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}>
            Official <span className="text-amber-500">Playlist</span>
          </h2>
        </div>

        <div className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Now Playing Header — Apple Music style */}
          <div className="relative p-5 md:p-8 bg-gradient-to-r from-zinc-900 to-black border-b border-zinc-800">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 overflow-hidden shadow-2xl shadow-amber-500/30 ring-1 ring-white/10">
                  {track?.thumbnail_url ? (
                    <img src={track.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <img src={LOGO_URL} alt="" className="w-full h-full object-contain p-3" />
                  )}
                </div>
                {isPlaying && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-black rounded-full flex items-center justify-center border border-amber-500">
                    <div className="flex items-end gap-0.5">
                      <span className="w-0.5 h-2 bg-amber-500 animate-pulse" />
                      <span className="w-0.5 h-3 bg-amber-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <span className="w-0.5 h-2 bg-amber-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-1">Now Playing</p>
                <h3 data-testid="home-player-track-title" className="text-xl md:text-3xl font-heading font-bold truncate">{track?.title}</h3>
                <p className="text-xs md:text-sm text-zinc-400 truncate mt-1">{track?.description || 'Official With The Whistle'}</p>
              </div>
              <button
                data-testid="home-player-play-btn"
                onClick={onPlayPause}
                className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-amber-500 hover:bg-amber-400 text-black rounded-full flex items-center justify-center shadow-xl shadow-amber-500/30 hover:scale-105 transition-all"
              >
                {isPlaying ? <Pause className="w-5 h-5 md:w-7 md:h-7" /> : <Play className="w-5 h-5 md:w-7 md:h-7 ml-0.5" />}
              </button>
            </div>
          </div>

          {/* Track list */}
          <div className="max-h-[380px] md:max-h-[440px] overflow-y-auto">
            {playlist.map((t, idx) => (
              <button
                key={t.id || idx}
                data-testid={`playlist-track-${idx}`}
                onClick={() => onSelect(idx)}
                className={`w-full text-left px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 md:gap-4 border-b border-zinc-900/60 last:border-b-0 transition-colors ${
                  idx === currentIndex ? 'bg-amber-500/10' : 'hover:bg-zinc-900/50'
                }`}
              >
                <div className="w-6 md:w-8 text-center text-[10px] md:text-xs font-mono text-zinc-500 flex-shrink-0">
                  {idx === currentIndex && isPlaying ? (
                    <div className="flex items-end gap-0.5 justify-center">
                      <span className="w-0.5 h-2 md:h-3 bg-amber-500 animate-pulse" />
                      <span className="w-0.5 h-3 md:h-4 bg-amber-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <span className="w-0.5 h-2 md:h-3 bg-amber-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </div>
                  ) : (
                    String(idx + 1).padStart(2, '0')
                  )}
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-gradient-to-br from-amber-500/40 to-orange-600/40 overflow-hidden flex-shrink-0">
                  {t.thumbnail_url ? (
                    <img src={t.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Music className="w-4 h-4 md:w-5 md:h-5 text-black/50" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm md:text-base font-medium truncate ${idx === currentIndex ? 'text-amber-400' : 'text-white'}`}>{t.title}</p>
                  <p className="text-[11px] md:text-xs text-zinc-500 truncate">{t.description || 'Official With The Whistle'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Podcast platforms
const PODCAST_LINKS = [
  { name: 'Spotify', url: 'https://open.spotify.com/', color: '#1DB954', icon: 'spotify' },
  { name: 'Apple Podcasts', url: 'https://podcasts.apple.com/', color: '#A020F0', icon: 'apple' },
  { name: 'YouTube', url: 'https://youtu.be/TDJ1PrZEkWI', color: '#FF0000', icon: 'youtube' },
];

const PodcastIcon = ({ type, className = "w-5 h-5" }) => {
  if (type === 'spotify') return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.421-1.02.599-1.56.3z"/></svg>
  );
  if (type === 'apple') return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 1 0 12 12A12 12 0 0 0 12 0zm0 16a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm0-7.5a4.5 4.5 0 0 0-3.375 7.492l1.17-1.17a3 3 0 1 1 4.41 0l1.17 1.17A4.5 4.5 0 0 0 12 8.5zm6.49 9.5l-1.23-1.23a7.5 7.5 0 1 0-10.52 0L5.51 18a9 9 0 1 1 12.98 0z"/></svg>
  );
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
  );
};

const PodcastStrip = ({ compact = false }) => (
  <div className={compact ? "flex items-center gap-2" : "flex items-center gap-2 md:gap-3"}>
    {PODCAST_LINKS.map(p => (
      <a
        key={p.name}
        href={p.url}
        target="_blank"
        rel="noreferrer"
        data-testid={`podcast-link-${p.icon}`}
        className="group flex items-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-black/70 hover:bg-black border border-zinc-800 hover:border-amber-500/60 rounded-full transition-all backdrop-blur-sm"
        style={{ boxShadow: `0 4px 14px rgba(0,0,0,0.4)` }}
      >
        <span className="transition-transform group-hover:scale-110" style={{ color: p.color }}>
          <PodcastIcon type={p.icon} className="w-4 h-4 md:w-5 md:h-5" />
        </span>
        <span className="text-xs md:text-sm font-bold text-white whitespace-nowrap hidden sm:inline">{p.name}</span>
      </a>
    ))}
  </div>
);

// ============ Banner Slider (standalone, can be placed anywhere) ============
const BannerSlider = ({ slides, sliderDurationSeconds }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (paused || !slides || slides.length === 0) return;
    const ms = Math.max(2, sliderDurationSeconds || 5) * 1000;
    const t = setInterval(() => {
      setCurrent(p => (p + 1) % slides.length);
    }, ms);
    return () => clearInterval(t);
  }, [paused, slides, sliderDurationSeconds]);

  const go = (dir) => {
    if (!slides || slides.length === 0) return;
    setCurrent(c => (c + dir + slides.length) % slides.length);
    setPaused(true);
    setTimeout(() => setPaused(false), 8000);
  };

  if (!slides || slides.length === 0) return null;

  return (
    <div
      className="relative w-full bg-black"
      onMouseEnter={() => { setPaused(true); setHover(true); }}
      onMouseLeave={() => { setPaused(false); setHover(false); }}
      data-testid="hero-banner-slider"
    >
      <div className="relative w-full">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`${i === current ? 'block' : 'hidden'} w-full`}
            data-testid={`banner-slide-${i}`}
          >
            <img
              src={slide.url}
              alt={slide.alt || 'Banner'}
              className="w-full h-auto block"
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => go(-1)}
        aria-label="Previous"
        data-testid="banner-prev-btn"
        className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-black/70 hover:bg-amber-500 hover:text-black text-white rounded-full backdrop-blur-sm border border-white/20 z-10 transition-opacity duration-300 ${hover ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"/></svg>
      </button>
      <button
        onClick={() => go(1)}
        aria-label="Next"
        data-testid="banner-next-btn"
        className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-black/70 hover:bg-amber-500 hover:text-black text-white rounded-full backdrop-blur-sm border border-white/20 z-10 transition-opacity duration-300 ${hover ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"/></svg>
      </button>
    </div>
  );
};

// ============ Banner Hero (feature photo w/ text overlay — no slideshow) ============
const BannerHero = ({ featurePhoto, onNeedRef }) => {
  return (
    <section className="bg-black">
      {featurePhoto?.url ? (
        <div
          className="relative w-full overflow-hidden bg-black"
          data-testid="feature-photo-section"
          style={{ minHeight: 'clamp(640px, 80vh, 900px)' }}
        >
          <img
            src={featurePhoto.url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: 'brightness(1.2) contrast(1.08) saturate(1.18)',
              objectPosition: 'center 30%',
              transform: 'scale(0.92)',
              transformOrigin: 'center center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          <div className="relative z-10 flex flex-col pt-20 md:pt-24 lg:pt-28 pb-8 md:pb-12 min-h-full">
            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 w-full flex-1 flex flex-col justify-between gap-6 md:gap-10">
              {/* TOP — Logo */}
              <div className="max-w-3xl">
                <img
                  src={LOGO_WHITE_URL}
                  alt="Official With The Whistle Logo"
                  fetchpriority="high"
                  decoding="async"
                  loading="eager"
                  className="w-32 h-32 sm:w-44 sm:h-44 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain"
                  style={{ filter: 'drop-shadow(0 8px 30px rgba(245,158,11,0.5)) drop-shadow(0 4px 15px rgba(0,0,0,0.8))' }}
                  data-testid="hero-logo"
                />
              </div>

              {/* MIDDLE — Headline + tagline */}
              <div className="max-w-3xl">
                <h1
                  className="font-heading font-black uppercase tracking-tighter leading-[0.88] mb-5 md:mb-6 text-white"
                  style={{
                    fontSize: 'clamp(2.5rem, 9vw, 8rem)',
                    textShadow: '0 4px 30px rgba(0,0,0,0.9), 0 2px 10px rgba(245, 158, 11, 0.2)'
                  }}
                >
                  Your Game's <br />
                  <span
                    className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500"
                    style={{ textShadow: 'none', filter: 'drop-shadow(0 4px 20px rgba(245, 158, 11, 0.5))' }}
                  >Not Official</span> <br />
                  <span className="text-zinc-200">Without Us.</span>
                </h1>
                <p className="text-sm md:text-lg lg:text-xl text-zinc-100 max-w-2xl leading-relaxed font-medium drop-shadow-lg">
                  <span className="text-amber-400 font-bold">80+ certified officials.</span> <span className="text-amber-400 font-bold">20+ years experience.</span> <span className="text-amber-400 font-bold">8 states.</span> From AAU to high school, college camps to pro-am — we bring the whistle.
                </p>
              </div>

              {/* BOTTOM — EST badge + Listen On + CTAs */}
              <div className="max-w-3xl space-y-4 md:space-y-5" data-testid="hero-podcast-strip">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/15 border border-amber-500/50 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase text-amber-400 shadow-lg shadow-amber-500/20">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    Est. 2015
                  </span>
                  <span className="text-[10px] md:text-xs font-bold tracking-[0.25em] uppercase text-zinc-300 drop-shadow">Denver, Colorado</span>
                </div>
                <div>
                  <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-amber-500 mb-2.5 drop-shadow">
                    ▸ Listen On
                  </p>
                  <PodcastStrip />
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 pt-2">
                  <Link to="/register?role=referee" className="w-full sm:w-auto">
                    <Button size="lg" data-testid="hero-become-ref-btn" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black uppercase px-8 md:px-10 h-12 md:h-14 text-base md:text-lg tracking-wide rounded-sm shadow-xl shadow-amber-500/40 hover:shadow-amber-500/60 hover:scale-[1.02] transition-all">
                      Become a Referee
                    </Button>
                  </Link>
                  <Button
                    size="lg"
                    data-testid="hero-need-ref-btn"
                    variant="outline"
                    onClick={() => onNeedRef()}
                    className="w-full sm:w-auto bg-black/40 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-black font-black uppercase px-8 md:px-10 h-12 md:h-14 text-base md:text-lg tracking-wide rounded-sm hover:scale-[1.02] transition-all"
                  >
                    Need a Ref?
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Fallback if no feature photo: plain hero section
        <div className="relative overflow-hidden border-t-2 border-amber-500/30">
          <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, #f59e0b 1.5px, transparent 1.5px)', backgroundSize: '40px 40px' }} />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-14 md:py-20">
            <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.88] mb-7" style={{ fontSize: 'clamp(3rem, 11vw, 9rem)' }}>
              Your Game's <br />
              <span className="text-amber-500">Not Official</span> <br />
              <span className="text-zinc-300">Without Us.</span>
            </h1>
          </div>
        </div>
      )}
    </section>
  );
};

// ============ Photo Gallery ============
const PhotoGallery = ({ photos }) => {
  const [selected, setSelected] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((photo, i) => (
          <button
            key={i}
            data-testid={`gallery-photo-${i}`}
            onClick={() => setSelected(photo)}
            className="group relative aspect-square bg-zinc-900 overflow-hidden rounded-lg"
          >
            <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-2 group-hover:translate-y-0 transition-transform opacity-0 group-hover:opacity-100">
              <p className="text-sm font-semibold text-white">{photo.caption}</p>
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <button className="absolute top-6 right-6 p-2 text-white hover:text-amber-500" onClick={() => setSelected(null)}>
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img src={selected.url} alt={selected.caption} className="max-w-full max-h-[80vh] object-contain rounded" />
            <p className="text-center mt-4 text-lg text-zinc-300">{selected.caption}</p>
          </div>
        </div>
      )}
    </>
  );
};

// ============ Need a Ref Form ============
const NeedRefInquiry = ({ initialSport = '' }) => {
  const [formData, setFormData] = useState({
    organization: '', contact_name: '', email: '', phone: '',
    sport: initialSport, event_date: '', location: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitted(true);
      toast.success("Inquiry submitted! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="inquiry-success" className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-emerald-400">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-heading font-bold mb-2">Thank You!</h3>
        <p className="text-zinc-400">We'll contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="inquiry-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input data-testid="inquiry-organization" placeholder="Organization *" value={formData.organization} onChange={e => setFormData({...formData, organization: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
        <Input data-testid="inquiry-contact-name" placeholder="Contact Name *" value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
        <Input data-testid="inquiry-email" type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
        <Input data-testid="inquiry-phone" placeholder="Phone *" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
        <Select value={formData.sport} onValueChange={v => setFormData({...formData, sport: v})}>
          <SelectTrigger data-testid="inquiry-sport" className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Sport" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="basketball">Basketball</SelectItem>
            <SelectItem value="football">Football</SelectItem>
            <SelectItem value="soccer">Soccer</SelectItem>
            <SelectItem value="volleyball">Volleyball</SelectItem>
            <SelectItem value="baseball">Baseball</SelectItem>
            <SelectItem value="softball">Softball</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="inquiry-date" type="date" value={formData.event_date} onChange={e => setFormData({...formData, event_date: e.target.value})} className="bg-zinc-800 border-zinc-700" />
      </div>
      <Input data-testid="inquiry-location" placeholder="Location (City, State)" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="bg-zinc-800 border-zinc-700" />
      <Textarea data-testid="inquiry-message" placeholder="Tell us about your event..." value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="bg-zinc-800 border-zinc-700 min-h-[100px]" />
      <Button type="submit" disabled={submitting} data-testid="inquiry-submit-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide">
        {submitting ? 'Submitting...' : 'Submit Inquiry'}
      </Button>
    </form>
  );
};

// ============ Main HomePage ============
export const HomePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Need-a-Ref modal state (with optional pre-selected sport)
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquirySport, setInquirySport] = useState('');
  const openInquiry = (sport = '') => {
    setInquirySport(sport);
    setInquiryOpen(true);
  };

  // Site settings (admin-editable)
  const [settings, setSettings] = useState({
    slider_duration_seconds: 5,
    banner_slides: [],
    feature_photo: null,
    gallery_photos: []
  });

  // Playlist state — unified via MusicContext (single audio element across all pages)
  const {
    playlist,
    currentIndex,
    isPlaying,
    isMuted,
    volume,
    setIsMuted,
    setVolume,
    playPause,
    selectTrack,
  } = useMusic();

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => {
      setSettings(prev => ({ ...prev, ...res.data }));
    }).catch(err => console.error('Failed to load site settings', err));
  }, []);

  const handleDashboard = () => {
    if (user?.role === 'referee') navigate('/referee-portal');
    else if (user?.role === 'client') navigate('/client-portal');
    else if (user?.role === 'fan') {
      // Fans have no dashboard — scroll to content
      document.getElementById('music')?.scrollIntoView({ behavior: 'smooth' });
    }
    else navigate('/dashboard');
  };

  const scrollToPlaylist = () => {
    document.getElementById('music')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img src={LOGO_YELLOW_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-9 h-9 sm:w-10 sm:h-10" />
              <div className="hidden sm:block">
                <p className="font-heading font-bold text-xs sm:text-sm uppercase tracking-wider leading-none">Official</p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-amber-500 leading-none mt-1">With The Whistle</p>
              </div>
            </Link>

            {/* Header Music Player Widget — hidden when no tracks */}
            {playlist.length > 0 && (
              <div
                data-testid="header-music-widget"
                className="flex items-center gap-1 md:gap-2 bg-zinc-900/80 border border-zinc-800 rounded-full px-2 py-1 md:px-3 md:py-1.5 backdrop-blur-sm flex-1 max-w-[280px] md:max-w-sm"
              >
                <button
                  onClick={playPause}
                  data-testid="header-playpause-btn"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center flex-shrink-0 transition-all hover:scale-105"
                >
                  {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                </button>
                <button
                  onClick={scrollToPlaylist}
                  data-testid="header-track-btn"
                  className="flex-1 min-w-0 text-left px-1 group"
                  title="Go to playlist"
                >
                  <p className="text-[10px] md:text-xs font-semibold text-white truncate group-hover:text-amber-400 transition-colors">
                    {playlist[currentIndex]?.title || 'Now Playing'}
                  </p>
                  <p className="text-[9px] md:text-[10px] text-zinc-500 truncate uppercase tracking-wider">
                    {isPlaying ? (isMuted ? '● muted' : '● playing') : '○ paused'}
                  </p>
                </button>
                <button
                  onClick={() => setIsMuted(m => !m)}
                  data-testid="header-mute-btn"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0 transition-colors"
                >
                  {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={volume}
                  onChange={e => setVolume(parseFloat(e.target.value))}
                  data-testid="header-volume-slider"
                  aria-label="Volume"
                  className="hidden md:block w-16 lg:w-20 h-1 accent-amber-500 cursor-pointer"
                />
              </div>
            )}

            <div className="hidden lg:flex items-center gap-5 text-sm flex-shrink-0">
              <a href="#services" className="text-zinc-400 hover:text-white transition-colors">Services</a>
              <a href="#about" className="text-zinc-400 hover:text-white transition-colors">About</a>
              <Link to="/team" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-team-link">Team</Link>
              <a href="#music" className="text-zinc-400 hover:text-white transition-colors">Playlist</a>
              <a href="#gallery" className="text-zinc-400 hover:text-white transition-colors">Gallery</a>
            </div>
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              {user ? (
                <>
                  <span className="text-xs text-zinc-500 hidden md:inline">Hi, {user.name?.split(' ')[0]}</span>
                  <Button onClick={handleDashboard} data-testid="nav-dashboard-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-bold h-9 px-4">
                    {user.role === 'fan' ? 'Watch Content' : 'My Dashboard'}
                  </Button>
                  <Button variant="ghost" onClick={logout} data-testid="nav-logout-btn" className="text-zinc-400 hover:text-white h-9">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="nav-login-btn" className="text-zinc-300 hover:text-white h-9">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="nav-register-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-bold h-9 px-4">Join</Button>
                  </Link>
                </>
              )}
            </div>
            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(o => !o)}
              className="sm:hidden p-2 text-zinc-300 hover:text-white"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile drawer */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu-drawer" className="sm:hidden border-t border-zinc-900 bg-black/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-1">
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 rounded">Services</a>
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 rounded">About</a>
              <Link to="/team" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 rounded" data-testid="mobile-nav-team-link">Meet the Team</Link>
              <a href="#music" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 rounded">Playlist</a>
              <a href="#gallery" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 rounded">Gallery</a>
              <div className="pt-3 mt-3 border-t border-zinc-900 flex flex-col gap-2">
                {user ? (
                  <>
                    <Button onClick={() => { setMobileMenuOpen(false); handleDashboard(); }} data-testid="mobile-nav-dashboard-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
                      {user.role === 'fan' ? 'Watch Content' : 'My Dashboard'}
                    </Button>
                    <Button variant="outline" onClick={() => { setMobileMenuOpen(false); logout(); }} data-testid="mobile-nav-logout-btn" className="w-full border-zinc-700 text-white">
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" data-testid="mobile-nav-login-btn" className="w-full border-zinc-700 text-white">Login</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button data-testid="mobile-nav-register-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">Join Us</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero — feature photo + text only */}
      <section>
        <BannerHero
          featurePhoto={settings.feature_photo}
          onNeedRef={() => openInquiry('')}
        />
      </section>

      {/* Stats strip */}
      <section className="bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 text-black border-y-4 border-amber-700 shadow-2xl shadow-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div data-testid="stat-officials"><p className="text-4xl md:text-6xl font-heading font-black drop-shadow-md">80+</p><p className="text-xs md:text-sm font-black uppercase tracking-[0.2em] mt-1 md:mt-2">Officials</p></div>
            <div data-testid="stat-years"><p className="text-4xl md:text-6xl font-heading font-black drop-shadow-md">20+</p><p className="text-xs md:text-sm font-black uppercase tracking-[0.2em] mt-1 md:mt-2">Years Exp.</p></div>
            <div data-testid="stat-states"><p className="text-4xl md:text-6xl font-heading font-black drop-shadow-md">8</p><p className="text-xs md:text-sm font-black uppercase tracking-[0.2em] mt-1 md:mt-2">States</p></div>
            <div data-testid="stat-games"><p className="text-4xl md:text-6xl font-heading font-black drop-shadow-md">1K+</p><p className="text-xs md:text-sm font-black uppercase tracking-[0.2em] mt-1 md:mt-2">Games Officiated</p></div>
          </div>
        </div>
      </section>

      {/* Banner Slider — moved here, right above In Action */}
      <section className="bg-black">
        <BannerSlider
          slides={settings.banner_slides && settings.banner_slides.length > 0 ? settings.banner_slides : BANNER_SLIDES}
          sliderDurationSeconds={settings.slider_duration_seconds || 5}
        />
      </section>

      {/* YouTube + Highlight Video — moved up right below stats */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-black to-zinc-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase text-amber-400 mb-3">
              Watch
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}>
              In <span className="text-amber-500">Action</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="aspect-video rounded-lg md:rounded-xl overflow-hidden bg-black shadow-2xl border border-zinc-800">
              <iframe
                src="https://www.youtube.com/embed/TDJ1PrZEkWI"
                title="Official With The Whistle"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="youtube-embed"
              />
            </div>
            <div className="aspect-video rounded-lg md:rounded-xl overflow-hidden bg-black shadow-2xl border border-zinc-800">
              <video
                src={HERO_VIDEO}
                controls
                playsInline
                poster={VIDEO_POSTER}
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid="hero-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-12 md:py-20 bg-zinc-950 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-14">
            <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase text-amber-400 mb-3">
              What We Do
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight leading-[1.05]" style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3.25rem)' }}>
              Certified Officials, <span className="text-amber-500">All Sports</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-400 mt-4 max-w-2xl mx-auto font-medium">From youth leagues to pro-am tournaments, we provide trained, certified officials across every major sport.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {SERVICES.map((s) => (
              <button
                key={s.name}
                data-testid={`service-${s.name.toLowerCase()}`}
                onClick={() => openInquiry(s.name.toLowerCase())}
                type="button"
                className={`group relative overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br ${s.color} p-5 md:p-10 hover:scale-[1.03] transition-all cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-xl hover:shadow-2xl min-h-[160px] md:min-h-[220px] flex flex-col justify-between`}
              >
                <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 text-7xl md:text-9xl opacity-25 group-hover:opacity-50 group-hover:scale-110 transition-all select-none">
                  {s.emoji}
                </div>
                <div className="relative z-10">
                  <h3
                    className="font-heading font-black uppercase tracking-tight leading-[0.95] drop-shadow-md"
                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}
                  >
                    {s.name}
                  </h3>
                  <p className="text-xs md:text-base text-white/85 mt-2 font-medium">{s.description}</p>
                </div>
                <div className="relative z-10 mt-4 md:mt-5 inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-black/25 backdrop-blur-sm rounded-full w-fit group-hover:bg-black/50 transition-colors">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-white">
                    Request Officials
                  </span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 text-white group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Respect The Whistle — Poetic Statement */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-zinc-950 via-black to-zinc-950">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url("${PHOTO_ALBUM[2].url}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(8px) grayscale(80%)',
              opacity: 0.35
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        </div>
        {/* Grunge overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-screen"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-block px-4 py-1.5 bg-amber-500/15 border border-amber-500/40 rounded-full text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-amber-400 mb-4">
              The Philosophy
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight" style={{ fontSize: 'clamp(2.5rem, 7vw, 5rem)' }}>
              Respect The <span className="text-amber-500">Whistle</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            {/* Photo */}
            <div className="lg:col-span-2">
              <div className="relative group">
                <div className="absolute inset-0 bg-amber-500/30 blur-2xl -z-10" />
                <div className="relative aspect-[3/4] max-w-xs mx-auto lg:max-w-none rounded-xl overflow-hidden shadow-2xl border-2 border-amber-500/30">
                  <img src={PHOTO_ALBUM[0].url} alt="Jeff Jazz Davis" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-400">Jeff Jazz Davis</p>
                    <p className="text-sm md:text-base font-heading uppercase text-white mt-1">CEO · Founder · Official</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Poem */}
            <div className="lg:col-span-3 space-y-3 md:space-y-5 text-left">
              <div className="space-y-1 mb-4">
                <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight">
                  "One of the <span className="text-amber-400">hardest jobs</span> in basketball?
                </p>
                <p className="text-3xl md:text-4xl lg:text-5xl font-heading font-black text-amber-500 leading-tight">
                  Officiating <span className="text-white">the game."</span>
                </p>
              </div>
              <div className="text-lg md:text-xl text-zinc-200 leading-relaxed space-y-3 md:space-y-4 font-medium pl-4 border-l-2 border-amber-500/30">
                <p>The pace is fast.<br />Everything is happening at once.</p>
                <p>You can know every rule in the book…<br />but making that call <span className="text-amber-400 font-bold italic">"in real time"</span>?<br />That's a <span className="text-amber-400 font-bold">different level.</span></p>
                <p>And <span className="text-amber-400 font-bold">no matter what</span> you call—<br />somebody in that gym<br />saw it a completely different way.</p>
                <p className="text-amber-400 font-bold text-xl md:text-2xl">That's the reality.</p>
                <p>So respect the ones who<br />step on that floor<br />and take on that<br />responsibility anyway.</p>
              </div>
              <div className="pt-4 md:pt-6 space-y-1">
                <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-black uppercase text-amber-500 leading-tight">Respect the game.</p>
                <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-black uppercase text-amber-400 leading-tight">Respect yourself.</p>
                <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-black uppercase text-white leading-tight">Respect the <span className="text-amber-500">whistle.</span></p>
              </div>
              <div className="pt-4 flex items-center gap-3">
                <img src={LOGO_YELLOW_URL} alt="" className="w-10 h-10 md:w-12 md:h-12" />
                <div>
                  <p className="text-xs md:text-sm font-bold text-amber-400 uppercase tracking-wider">— Jeff Jazz Davis</p>
                  <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-[0.2em]">Official With The Whistle</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About / Bio — Story */}
      <section id="about" className="py-12 md:py-20 bg-black relative overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full opacity-10 hidden md:block pointer-events-none">
          <img src={LOGO_YELLOW_URL} alt="" className="absolute -top-20 -right-40 w-[600px] opacity-30" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-amber-400 mb-3">
              Our Story
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight leading-[0.95]" style={{ fontSize: 'clamp(1.75rem, 5vw, 3.5rem)' }}>
              Built From The <span className="text-amber-500">Ground Up</span>
            </h2>
          </div>

          {/* Mixed photo + bio layout */}
          <div className="space-y-8 md:space-y-12 text-zinc-200 leading-relaxed text-base md:text-lg max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="aspect-[4/5] rounded-xl overflow-hidden border-2 border-amber-500/20 shadow-xl shadow-amber-500/10">
                  <img src={PHOTO_ALBUM[0].url} alt="Jazz" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="md:col-span-3">
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-amber-500 mb-2">The Beginning</p>
                <p>{BIO_PARAGRAPHS[0]}</p>
                <p className="mt-4">{BIO_PARAGRAPHS[1]}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-3 order-2 md:order-1">
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-amber-500 mb-2">The Journey</p>
                <p>{BIO_PARAGRAPHS[2]}</p>
                <p className="mt-4">{BIO_PARAGRAPHS[3]}</p>
              </div>
              <div className="md:col-span-2 order-1 md:order-2">
                <div className="aspect-[4/5] rounded-xl overflow-hidden border-2 border-amber-500/20 shadow-xl shadow-amber-500/10">
                  <img src={PHOTO_ALBUM[4].url} alt="Team" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="aspect-[4/5] rounded-xl overflow-hidden border-2 border-amber-500/20 shadow-xl shadow-amber-500/10">
                  <img src={PHOTO_ALBUM[2].url} alt="Game Day" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="md:col-span-3">
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-amber-500 mb-2">The Stage</p>
                <p>{BIO_PARAGRAPHS[4]}</p>
                <p className="mt-4">{BIO_PARAGRAPHS[5]}</p>
              </div>
            </div>

            <div className="mt-8 md:mt-12 p-6 md:p-8 bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent border-l-4 border-amber-500 rounded-r-lg">
              <p className="text-xl md:text-2xl lg:text-3xl italic text-amber-100 font-heading leading-snug">
                "Your game's not official unless you have Official With The Whistle!"
              </p>
              <p className="text-sm md:text-base text-amber-400 mt-3 font-bold uppercase tracking-widest">— Jazz</p>
            </div>

            <div className="mt-10 text-center">
              <Link to="/team">
                <Button size="lg" data-testid="about-meet-team-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wide px-8 md:px-10 h-12 md:h-14 rounded-sm shadow-xl shadow-amber-500/30 hover:scale-[1.02] transition-all">
                  Meet The Team <span className="ml-2">→</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Music Playlist */}
      <HomePlaylistSection
        playlist={playlist}
        currentIndex={currentIndex}
        isPlaying={isPlaying}
        onPlayPause={playPause}
        onSelect={selectTrack}
      />

      {/* Photo Gallery */}
      <section id="gallery" className="py-12 md:py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-block px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase text-amber-400 mb-3">
              Moments
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}>
              Photo <span className="text-amber-500">Gallery</span>
            </h2>
          </div>
          <PhotoGallery photos={settings.gallery_photos && settings.gallery_photos.length > 0 ? settings.gallery_photos : PHOTO_ALBUM} />
        </div>
      </section>

      {/* Partners */}
      <section className="py-10 md:py-16 bg-black border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6 md:mb-8">Trusted By</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              "Denver Public Schools", "Aurora Public Schools", "Littleton Schools",
              "EYBL", "Under Armour", "Adidas Classic", "BIGFOOT EVENTS", "MAYB",
              "JAM ON IT", "Ball Is Life", "West Coast Elite", "JR Nuggets", "PAL Football"
            ].map((p) => (
              <span key={p} className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-[11px] md:text-xs font-medium text-zinc-400 rounded-full hover:border-amber-500/50 hover:text-white transition-all">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Need a Ref? CTA */}
      <section className="relative py-14 md:py-24 bg-gradient-to-br from-amber-600 via-amber-500 to-orange-600 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,0,0,0.3) 0, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.3) 0, transparent 50%)'
        }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={LOGO_URL} alt="Logo" className="w-20 h-20 md:w-28 md:h-28 mx-auto mb-4 md:mb-6 drop-shadow-2xl" />
          <h2 className="font-heading font-black uppercase tracking-tight text-black mb-4 md:mb-5 drop-shadow-lg" style={{ fontSize: 'clamp(2.5rem, 7vw, 5rem)' }}>
            Ready To Get Started?
          </h2>
          <p className="text-base md:text-xl text-black/90 mb-6 md:mb-10 max-w-2xl mx-auto font-bold">
            Whether you want to become a certified official or need professional referees for your event — we've got you covered.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Link to="/register?role=referee" className="w-full sm:w-auto">
              <Button size="lg" data-testid="cta-join-btn" className="w-full bg-black hover:bg-zinc-900 text-white font-bold uppercase px-8 md:px-10 h-12 md:h-14 tracking-wide rounded-sm">
                Join Our Team
              </Button>
            </Link>
            <Button size="lg" data-testid="cta-need-ref-btn" variant="outline" onClick={() => openInquiry('')} className="w-full sm:w-auto bg-white/10 border-2 border-black text-black hover:bg-black hover:text-white font-bold uppercase px-8 md:px-10 h-12 md:h-14 tracking-wide rounded-sm">
              Book Officials
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-14 bg-zinc-950 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_YELLOW_URL} alt="Logo" className="w-12 h-12" />
                <div>
                  <p className="font-heading font-bold uppercase text-sm leading-none">Official With</p>
                  <p className="font-heading font-bold uppercase text-amber-500 text-sm leading-none mt-1">The Whistle</p>
                </div>
              </div>
              <p className="text-xs md:text-sm text-zinc-500">Your game's not official unless you have Official With The Whistle.</p>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4">Contact</h4>
              <div className="space-y-3">
                <a href="tel:7203384725" className="flex items-center gap-3 text-sm text-zinc-300 hover:text-amber-500 transition-colors group">
                  <span className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:border-amber-500 transition-all">
                    <Phone className="w-4 h-4 text-amber-500 group-hover:text-black" />
                  </span>
                  <span className="break-all">720.338.4725</span>
                </a>
                <a href="mailto:jazzdavis21@yahoo.com" className="flex items-center gap-3 text-sm text-zinc-300 hover:text-amber-500 transition-colors group">
                  <span className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:border-amber-500 transition-all">
                    <Mail className="w-4 h-4 text-amber-500 group-hover:text-black" />
                  </span>
                  <span className="break-all">jazzdavis21@yahoo.com</span>
                </a>
                <div className="flex items-center gap-3 text-sm text-zinc-300">
                  <span className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-amber-500" />
                  </span>
                  <span>Denver, Colorado</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link to="/login" className="block text-zinc-400 hover:text-white transition-colors">Login</Link>
                <Link to="/team" className="block text-zinc-400 hover:text-white transition-colors" data-testid="footer-team-link">Meet the Team</Link>
                <Link to="/register?role=referee" className="block text-zinc-400 hover:text-white transition-colors">Become a Referee</Link>
                <Link to="/register?role=client" className="block text-zinc-400 hover:text-white transition-colors">Register as Client</Link>
                <Link to="/register?role=fan" className="block text-zinc-400 hover:text-white transition-colors">Sign up as Fan</Link>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4">Follow</h4>
              <div className="flex flex-wrap gap-2">
                <a href="#" aria-label="Facebook" className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-full hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all"><Facebook className="w-4 h-4" /></a>
                <a href="#" aria-label="Instagram" className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-full hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all"><Instagram className="w-4 h-4" /></a>
                <a href="#" aria-label="Twitter" className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-full hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all"><Twitter className="w-4 h-4" /></a>
                <a href="https://youtu.be/TDJ1PrZEkWI" target="_blank" rel="noreferrer" aria-label="YouTube" className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-full hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all"><Youtube className="w-4 h-4" /></a>
              </div>
            </div>
          </div>
          <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-zinc-900 text-center text-xs text-zinc-600">
            <p>© {new Date().getFullYear()} Official With The Whistle LLC. All rights reserved.</p>
            <p className="mt-2">It's Official ☝🏾❤️</p>
          </div>
        </div>
      </footer>

      {/* Unified Need-a-Ref Inquiry Dialog (triggered from hero, services, CTA) */}
      <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl md:text-2xl uppercase">Need a Ref?</DialogTitle>
          </DialogHeader>
          <NeedRefInquiry key={inquirySport} initialSport={inquirySport} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
